"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.calculateNewCropForHeightChange = void 0;
const calculateNewCropForHeightChange = _ref => {
  let {
    oldLayer,
    oldCrop,
    newLayerSizeInPercent,
    surface,
    imageHeight
  } = _ref;
  var _a, _b;
  const isResizingBottom = oldLayer.y === newLayerSizeInPercent.y;
  //https://artifactteam.atlassian.net/wiki/spaces/EN/pages/2716794885/Image+Resizing+Crop+Math
  const newCropHeightInPixels = Math.round(oldCrop.height / oldLayer.height * newLayerSizeInPercent.height);
  const heightCropPixelsNeeded = newCropHeightInPixels - oldCrop.height;
  const newLayerAspectRatio = surface.height * newLayerSizeInPercent.height / (surface.width * newLayerSizeInPercent.width);
  if (heightCropPixelsNeeded > 0) {
    return calculateNewCropForIncreasedHeightChange({
      oldCrop,
      isResizingBottom,
      heightCropPixelsNeeded,
      newLayerAspectRatio,
      rotation: ((_b = (_a = oldLayer === null || oldLayer === void 0 ? void 0 : oldLayer.userData) === null || _a === void 0 ? void 0 : _a.modifications) === null || _b === void 0 ? void 0 : _b.rotation) || 0,
      imageHeight
    });
  }
  return calculateNewCropForDecreasedHeightChange({
    oldCrop,
    heightCropPixelsNeeded,
    isResizingBottom
  });
};
exports.calculateNewCropForHeightChange = calculateNewCropForHeightChange;
const calculateNewCropForDecreasedHeightChange = _ref2 => {
  let {
    oldCrop,
    heightCropPixelsNeeded,
    isResizingBottom
  } = _ref2;
  return {
    x: oldCrop.x,
    y: isResizingBottom ? oldCrop.y : oldCrop.y + Math.abs(heightCropPixelsNeeded),
    width: oldCrop.width,
    height: oldCrop.height - Math.abs(heightCropPixelsNeeded)
  };
};
/**
 * Calculates the new crop dimensions for an image when the height is increased. As the height increases,
 * if there's room of the resizing side, the crop will be adjusted so that more of the image is shown.
 * After the resizing side's crop hits the edge of the image, the other side's crop is expanded.
 * If the crop then takes the full image's height, the crop zooms in on the image.
 *
 * We do a similar operation on width.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Crop} params.oldCrop - The current crop dimensions.
 * @param {boolean} params.isResizingBottom - Flag indicating if the resizing is happening on the bottom side.
 * @param {number} params.heightCropPixelsNeeded - The additional height needed for the crop.
 * @param {number} params.imageHeight - The original height of the image.
 * @param {number} params.imageWellWidth - The display width of the image container.
 * @param {number} params.imageWellHeight - The display height of the image container.
 * @returns {Crop} The new crop dimensions.
 */
const calculateNewCropForIncreasedHeightChange = _ref3 => {
  let {
    oldCrop,
    isResizingBottom,
    heightCropPixelsNeeded,
    newLayerAspectRatio,
    rotation,
    imageHeight
  } = _ref3;
  const newCrop = {
    x: oldCrop.x,
    y: oldCrop.y,
    width: oldCrop.width,
    height: oldCrop.height
  };
  const bottomCropRemaining = rotation % (Math.PI / 4) != 0 ? 0 : imageHeight - (oldCrop.y + oldCrop.height);
  const topCropRemaining = rotation % (Math.PI / 4) != 0 ? 0 : oldCrop.y;
  // Updates newCrop and heightCropPixelsNeeded via closure
  const adjustCrop = (bottom, remaining) => {
    if (bottom) {
      const bottomCropToTake = Math.min(remaining, heightCropPixelsNeeded);
      newCrop.height += bottomCropToTake;
      heightCropPixelsNeeded -= bottomCropToTake;
    } else {
      const topCropToTake = Math.min(remaining, heightCropPixelsNeeded);
      newCrop.y -= topCropToTake;
      newCrop.height += topCropToTake;
      heightCropPixelsNeeded -= topCropToTake;
    }
  };
  if (isResizingBottom) {
    adjustCrop(true, bottomCropRemaining);
    adjustCrop(false, topCropRemaining);
  } else {
    adjustCrop(false, topCropRemaining);
    adjustCrop(true, bottomCropRemaining);
  }
  if (heightCropPixelsNeeded > 0) {
    //https://artifactteam.atlassian.net/wiki/spaces/EN/pages/2716794885/Image+Resizing+Crop+Math
    const newWidth = Math.round(newCrop.height / newLayerAspectRatio);
    const widthDiff = newCrop.width - newWidth;
    newCrop.width = newWidth;
    newCrop.x += Math.round(widthDiff / 2);
  }
  return newCrop;
};